import LegalLayout from "./LegalLayout";
import { COMPANY } from "@/legal/companyInfo";

const addr = `${COMPANY.address.street}, ${COMPANY.address.zip} ${COMPANY.address.city}, ${COMPANY.address.country}`;

export default function Terms() {
  return (
    <LegalLayout
      title="Conditions Générales de Vente"
      subtitle={`En vigueur au ${COMPANY.lastUpdated}. Applicables à tout utilisateur du service Oresto.`}
    >
      <section>
        <h2>1. Objet</h2>
        <p>
          Les présentes Conditions Générales de Vente (ci-après « <strong>CGV</strong> ») régissent la relation contractuelle entre :
        </p>
        <ul>
          <li><strong>{COMPANY.legalName}</strong> ({COMPANY.legalForm}), IDE {COMPANY.ide}, ayant son siège au {addr}, {COMPANY.registry}, ci-après « <strong>Oresto</strong> » ou « <strong>l'Éditeur</strong> » ;</li>
          <li>et toute personne physique ou morale exploitant un établissement de restauration en Suisse et souscrivant à l'offre logicielle Oresto, ci-après « <strong>le Client</strong> ».</li>
        </ul>
        <p>La souscription à l'offre Oresto emporte acceptation sans réserve des présentes CGV.</p>
      </section>

      <section>
        <h2>2. Description du service</h2>
        <p>Oresto est un logiciel de gestion (Software-as-a-Service, SaaS) accessible en ligne, destiné aux restaurateurs suisses, permettant notamment :</p>
        <ul>
          <li>la saisie quotidienne du chiffre d'affaires et le calcul automatique de la TVA aux taux officiels de l'Administration fédérale des contributions (AFC) ;</li>
          <li>le suivi des factures fournisseurs et de leurs échéances ;</li>
          <li>la génération de rapports au format PDF et CSV à destination de la fiduciaire ;</li>
          <li>le stockage sécurisé des données de gestion du Client.</li>
        </ul>
        <p>Le service est accessible via un navigateur web moderne, depuis un téléphone, une tablette ou un ordinateur.</p>
      </section>

      <section>
        <h2>3. Essai gratuit</h2>
        <p>
          Chaque nouveau Client bénéficie d'un essai gratuit de <strong>{COMPANY.trialDays} jours</strong> à compter de la création de son compte. Aucune carte bancaire n'est demandée pour activer cet essai.
        </p>
        <p>
          À l'issue de ces {COMPANY.trialDays} jours, l'accès aux fonctionnalités de saisie (caisses, factures, modifications de l'établissement) est automatiquement suspendu tant que le Client n'a pas souscrit un abonnement payant. Les fonctionnalités de <strong>consultation</strong> (accès à l'historique, téléchargement des rapports déjà générés) restent accessibles gratuitement.
        </p>
      </section>

      <section>
        <h2>4. Prix et paiement</h2>
        <p>Les tarifs sont exprimés en francs suisses (CHF). <strong>{COMPANY.vatNote}</strong> Ils sont les suivants :</p>
        <ul>
          <li>Formule mensuelle : <strong>{COMPANY.monthlyPrice} par mois</strong> ;</li>
          <li>Formule annuelle : <strong>{COMPANY.yearlyPrice} par an</strong> (soit deux mois offerts).</li>
        </ul>
        <p>Les Clients inscrits avec un code ambassadeur avant le changement de tarif conservent le tarif garanti lors de leur inscription (69 CHF par mois ou 690 CHF par an).</p>
        <p>Le paiement est effectué en ligne via <strong>Stripe Payments Europe, Ltd.</strong>, prestataire agréé pour le traitement des paiements. Oresto ne stocke aucune donnée de carte bancaire sur ses serveurs.</p>
        <p>Les moyens de paiement acceptés incluent notamment les cartes Visa, Mastercard, American Express, ainsi que TWINT, Apple Pay et Google Pay lorsque disponibles.</p>
        <p>L'abonnement est prélevé automatiquement au début de chaque période (mensuelle ou annuelle), sauf résiliation du Client dans les conditions de l'article 6.</p>
      </section>

      <section>
        <h2>5. Facturation</h2>
        <p>Un reçu détaillé est envoyé automatiquement par email au Client à chaque paiement. Chaque facture mentionne notamment son numéro, la date, la période couverte et le montant net ; Oresto Sàrl n'étant pas assujettie à la TVA, aucune TVA n'y figure. Toutes les factures sont téléchargeables en PDF dans l'espace Client (Plus → Abonnement).</p>
        <p>Le Client s'engage à conserver ces reçus pour ses propres obligations comptables et fiscales.</p>
      </section>

      <section>
        <h2>6. Durée et résiliation</h2>
        <p>L'abonnement est conclu pour une durée indéterminée. Il est renouvelé tacitement à chaque échéance (mensuelle ou annuelle).</p>
        <p>Le Client peut résilier son abonnement à tout moment, sans motif ni préavis, en un clic depuis son espace personnel (Plus → Abonnement → Résilier) ou par simple email adressé à <a href={`mailto:${COMPANY.contactEmail}`}>{COMPANY.contactEmail}</a>. La résiliation prend effet à l'échéance de la période payée en cours ; aucun remboursement au prorata n'est effectué pour la période entamée.</p>
        <p>Après résiliation, le Client conserve un accès en lecture seule à ses données pendant 90 jours, afin de lui permettre de télécharger ses rapports définitifs.</p>
        <p>Oresto se réserve le droit de suspendre ou de résilier immédiatement le compte d'un Client en cas de non-paiement, de fraude, ou de manquement grave aux présentes CGV.</p>
      </section>

      <section>
        <h2>7. Obligations du Client</h2>
        <p>Le Client s'engage à :</p>
        <ul>
          <li>fournir des informations exactes lors de la création du compte et les maintenir à jour ;</li>
          <li>conserver la confidentialité de ses identifiants de connexion ;</li>
          <li>utiliser le service conformément à son usage prévu, à savoir la gestion de son propre établissement de restauration ;</li>
          <li>ne pas tenter d'accéder aux données d'autres Clients, ni de porter atteinte à la sécurité ou au fonctionnement du service ;</li>
          <li>respecter la législation suisse en vigueur, notamment en matière fiscale et de tenue de comptabilité (Code des obligations, LTVA, ordonnances de l'AFC).</li>
        </ul>
      </section>

      <section>
        <h2>8. Propriété du contenu et données du Client</h2>
        <p>Le Client conserve <strong>l'entière propriété</strong> des données qu'il saisit dans Oresto (chiffres d'affaires, factures, informations de l'établissement, logo, etc.). Oresto n'exploite ces données que pour fournir le service, et ne les revend, ne les partage et ne les utilise pour aucune finalité commerciale ou publicitaire.</p>
        <p>Le Client peut à tout moment exporter l'intégralité de ses données au format PDF et CSV, ou demander leur suppression complète en écrivant à <a href={`mailto:${COMPANY.dpoEmail}`}>{COMPANY.dpoEmail}</a>.</p>
      </section>

      <section>
        <h2>9. Disponibilité du service</h2>
        <p>Oresto s'engage à mettre en œuvre les moyens raisonnables pour assurer une disponibilité continue du service. Aucun taux de disponibilité contractuel n'est toutefois garanti dans le cadre des présentes CGV. Le Client reconnaît que des interruptions ponctuelles peuvent survenir (maintenance, mise à jour, incident technique, force majeure).</p>
        <p>En cas d'interruption prolongée directement imputable à Oresto et dépassant 48 heures consécutives, un avoir sur l'abonnement en cours pourra être accordé au prorata de l'indisponibilité, sur demande du Client.</p>
      </section>

      <section>
        <h2>10. Limitation de responsabilité</h2>
        <p>Oresto fournit un outil d'aide à la gestion. Le Client demeure seul responsable de :</p>
        <ul>
          <li>l'exactitude et la complétude des données qu'il saisit ;</li>
          <li>la conformité de sa comptabilité vis-à-vis de l'AFC et de sa fiduciaire ;</li>
          <li>ses déclarations fiscales et sociales.</li>
        </ul>
        <p>Oresto ne pourra en aucun cas être tenu responsable des redressements fiscaux, pénalités, poursuites ou pertes commerciales que subirait le Client, sauf faute grave démontrée d'Oresto.</p>
        <p>La responsabilité d'Oresto, si elle était engagée, serait dans tous les cas limitée au montant des sommes versées par le Client au titre des douze (12) derniers mois précédant le fait générateur.</p>
      </section>

      <section>
        <h2>11. Propriété intellectuelle</h2>
        <p>Le logiciel Oresto, sa marque, son logo, ses interfaces et l'ensemble de ses composants demeurent la propriété exclusive de {COMPANY.legalName}. Toute reproduction, modification ou revente sans autorisation écrite préalable est interdite.</p>
      </section>

      <section>
        <h2>12. Données personnelles</h2>
        <p>Le traitement des données à caractère personnel du Client est décrit dans la <a href="/confidentialite">Politique de confidentialité</a>, qui fait partie intégrante des présentes CGV.</p>
      </section>

      <section>
        <h2>13. Modifications des CGV</h2>
        <p>Oresto se réserve le droit de modifier les présentes CGV. Toute modification substantielle sera notifiée au Client par email au moins 30 jours avant son entrée en vigueur. Le Client pourra alors résilier son abonnement sans frais s'il n'accepte pas les nouvelles conditions.</p>
      </section>

      <section>
        <h2>14. Droit applicable et juridiction</h2>
        <p>Les présentes CGV sont soumises au <strong>droit suisse</strong>. Tout litige relatif à leur interprétation ou leur exécution sera soumis à la compétence exclusive des <strong>{COMPANY.jurisdiction}</strong>, sous réserve d'un éventuel recours au Tribunal fédéral suisse.</p>
      </section>

      <section>
        <h2>15. Contact</h2>
        <p>Pour toute question relative aux présentes CGV :</p>
        <ul>
          <li>Email : <a href={`mailto:${COMPANY.contactEmail}`}>{COMPANY.contactEmail}</a></li>
          <li>Adresse postale : {COMPANY.legalName}, {addr}</li>
        </ul>
      </section>
    </LegalLayout>
  );
}
